<template>
  <div>
    <v-overlay :value="awaiting">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <CardsDialog />
    <PaymentDialog />
    <BackToCartDialog />

    <v-container class="px-0 px-sm-3 pb-sm-3" fluid>
      <checkout-header />

      <v-row no-gutters>
        <CustomizeOrder
          :addressState="addressState"
          :selectedTip="selectedTip"
          :setFullAddress="setFullAddress"
          :setNameAndPhoneValidity="setNameAndPhoneValidity"
          :setOrderAddress="setOrderAddress"
          :setSelectedTip="setSelectedTip"
          :setTipAmountOnChange="setTipAmountOnChange"
          :tipText="tipText"
          :validNameAndPhone="validNameAndPhone"
        />
        <SummarizeOrder
          :addressState="addressState"
          :products="products"
          :totals="totals"
          :validNameAndPhone="validNameAndPhone"
        />
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { packageProductsForOrderDetails, useSharedLibCart } from "@/util.js";
import { calculateCart } from "@/worker-api.js";
import { mapGetters, mapState } from "vuex";
import { AddressState } from "@/types/address.js";
import BackToCartDialog from "@/components/Order/Dialogs/BackToCart.vue";
import CardsDialog from "@/components/Order/Dialogs/Cards.vue";
import CustomizeOrder from "@/components/Order/Customize.vue";
import PaymentDialog from "@/components/Payment/Dialog.vue";
import SummarizeOrder from "@/components/Order/Summarize.vue";
import { Tips } from "@/types/tips.js";
import CheckoutHeader from "@/components/Checkout/CheckoutHeader.vue";

export default {
  name: "CheckoutView",
  components: {
    CheckoutHeader,
    BackToCartDialog,
    CardsDialog,
    CustomizeOrder,
    PaymentDialog,
    SummarizeOrder,
  },
  computed: {
    ...mapGetters("orders", {
      awaiting: "awaiting",
    }),
    ...mapState({
      cart: (state) => state.cart,
      order: (state) => state.orders.order,
      processingSavedCC: (state) => state.orders.processingSavedCC,
      sharedLibPromos: (state) => state.promotions.sharedLibPromos,
      storeInfo: (state) => state.store.info,
      user: (state) => state.user.info,
    }),
  },
  data() {
    return {
      addressState: null,
      products: [],
      selectedTip: null,
      tipAmount: null,
      tipText: null,
      totals: { ccFee: 0, fee: 0, saving: 0, subtotal: 0, tax: 0 },
      validNameAndPhone: true,
    };
  },
  methods: {
    setAddress(address) {
      this.$store.commit("orders/SET_ADDRESS", address);
      // need this if?
      if (address.full_address) {
        this.updateAddressState(address.full_address);
      }
    },
    setFullAddress(val) {
      this.$store.commit("cart/SET_ADDRESS_ERROR", false);
      this.$store.commit("orders/SET_FULL_ADDRESS", val);
      this.updateAddressState(val);
    },
    setNameAndPhoneValidity(valid) {
      this.validNameAndPhone = valid;
    },
    setOrderAddress(address) {
      this.$store.commit("orders/UPDATE", {
        name: "lastFormattedAddress",
        val: address.full_address,
      });
      address && address.address && this.setAddress(address);
    },
    setSelectedTip(selectedTip) {
      this.selectedTip = selectedTip;
      this.setSummary();
    },
    calculateTip(updated) {
      if (!this.order.service || this.order.service === "PICKUP") {
        return { cents: 0, coeff: null };
      }
      if (updated) return { ...Tips.other, cents: this.tipAmount };
      return this.selectedTip;
    },
    async setSummary(updated) {
      const deliveryAmount =
        this.order.service === "DELIVERY"
          ? this.storeInfo.delivery_amount
          : null;

      const sharedLibCart = useSharedLibCart(
        this.cart.products,
        this.sharedLibPromos,
        this.storeInfo.modifierGroups,
        deliveryAmount,
        this.cart.firstOrderDiscount,
        this.storeInfo.fee_buster_config,
        this.calculateTip(updated),
        this.storeInfo.points_config,
        this.storeInfo.price_increase,
      );

      const { items, totals } = await calculateCart(sharedLibCart);
      this.products = packageProductsForOrderDetails(items);
      this.totals = totals;

      !updated && this.setTipAmount(totals.driverTip);
      this.updateTipAmountOnOrder(totals.driverTip);
    },
    setTipAmount(cents) {
      this.tipText = cents.toString();
      this.tipAmount = parseInt(this.tipText.replace(/\D/g, ""));
      this.tipText = (this.tipAmount / 100).toFixed(2);
      this.tipText = isNaN(this.tipText) ? "0.00" : this.tipText;
    },
    setTipAmountOnChange(cents) {
      this.setTipAmount(cents);
      this.setSummary(true);
    },
    updateTipAmountOnOrder(cents) {
      this.$store.commit("orders/UPDATE", {
        name: "driverTip",
        val: cents,
      });
    },
    updateAddressState(fullAddress) {
      if (!fullAddress) {
        this.addressState = AddressState.empty;
      } else {
        if (fullAddress !== this.order.lastFormattedAddress) {
          this.addressState = AddressState.invalid;
        } else {
          if (this.cart.deliveryErrorMessage) {
            this.addressState = AddressState.error;
          } else {
            this.addressState = AddressState.valid;
          }
        }
      }
    },
  },
  async mounted() {
    await this.setSummary();
    this.$store.commit("orders/RESET_ORDER_STATE");
    const fullUserAddress =
      this.user && this.user.address && this.user.address.full_address;
    const fullOrderAddress =
      this.order && this.order.address && this.order.address.full_address;

    if (!fullUserAddress && !fullOrderAddress) {
      this.addressState = AddressState.empty;
      return;
    }

    fullOrderAddress
      ? this.setOrderAddress(this.order.address)
      : this.setOrderAddress(this.user.address);

    this.updateAddressState(fullOrderAddress || fullUserAddress);
  },
  watch: {
    "cart.products": function (products) {
      if (products.length && !this.cart.itemIdUpdated) {
        this.setSummary();
        return;
      }
      this.cart.itemIdUpdated &&
        this.$store.commit("cart/ITEM_ID_UPDATED", false);
      if (this.processingSavedCC || products.length) return;
      this.$router.push("/");
    },
    "order.service": function (val) {
      if (val === "DELIVERY" && this.storeInfo.delivery_name === "My Team")
        this.setSummary();
    },
    "storeInfo.delivery_amount": function (newVal, oldVal) {
      if (newVal != oldVal) this.setSummary();
    },
    isAppIdle: function () {
      this.$store.commit("orders/CLOSE_PAYMENT_DIALOG");
      this.$store.commit("orders/RESET_ORDER_STATE");
      this.$router.push("/");
    },
  },
};
</script>
